// AdminRails — admin-only cross-tenant bank-rails view.
//
// Route: /admin/rails, gated by RequireAuth role="admin". Backed by
// GET /v1/admin/bank-rails (admin-only). PDF round 3 N5.
//
// Built for the operator's "are two merchants accidentally sharing the
// same IDFC credentials?" debugging question. The merchant_key is
// rendered obfuscated server-side as `f****l` (first rune + four stars
// + last rune) — width is fixed regardless of length, so a duplicate
// triple shows up byte-identical across rows. Quickest way to find
// sharing: sort the table by merchant_key_obfuscated.
//
// Buckzy rails show with no credential block (Buckzy credentials live
// per-merchant, not per-rail).

import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { RailLimitsCard } from "@/components/RailLimitsCard";
import { Pill } from "@/components/UI";
import { listAdminBankRails, type AdminRail } from "@/api/admin";
import { providerLabel } from "@/utils/providerLabel";
import { formatDateTime } from "@/utils/format";

type SortKey =
  | "merchant"
  | "label"
  | "provider"
  | "environment"
  | "merchant_key"
  | "status";

export function AdminRails() {
  const [q, setQ] = useState("");
  const [providerFilter, setProviderFilter] = useState<string>("");
  const [includeDisabled, setIncludeDisabled] = useState(false);
  const [sortKey, setSortKey] = useState<SortKey>("merchant_key");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");

  const { data, isLoading, isError, refetch, isFetching } = useQuery({
    queryKey: ["admin-bank-rails"],
    queryFn: () => listAdminBankRails(),
  });

  // Stable reference for the dependency array — the inline `data?.rails ?? []`
  // form re-creates the empty array on every render which trips the
  // exhaustive-deps lint on every downstream useMemo.
  const rails = useMemo(() => data?.rails ?? [], [data]);

  // Derived view: filter then sort. Cheap to recompute — the row count is
  // bounded in the low hundreds across the platform.
  const view = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const filtered = rails.filter((r) => {
      if (!includeDisabled && r.status !== "active") return false;
      if (providerFilter && r.provider !== providerFilter) return false;
      if (needle === "") return true;
      const hay = [
        r.merchant_slug,
        r.merchant_display_name,
        r.label,
        r.provider,
        r.credential?.base_url ?? "",
        r.credential?.email_obfuscated ?? "",
        r.credential?.merchant_key_obfuscated ?? "",
      ]
        .join(" ")
        .toLowerCase();
      return hay.includes(needle);
    });

    const dir = sortDir === "asc" ? 1 : -1;
    const sorted = [...filtered].sort((a, b) => {
      switch (sortKey) {
        case "merchant":
          return (
            dir *
            (a.merchant_display_name.localeCompare(b.merchant_display_name) ||
              a.merchant_id - b.merchant_id)
          );
        case "label":
          return dir * a.label.localeCompare(b.label);
        case "provider":
          return dir * a.provider.localeCompare(b.provider);
        case "environment":
          return (
            dir *
            (a.credential?.environment ?? "").localeCompare(
              b.credential?.environment ?? "",
            )
          );
        case "merchant_key":
          return (
            dir *
            (a.credential?.merchant_key_obfuscated ?? "").localeCompare(
              b.credential?.merchant_key_obfuscated ?? "",
            )
          );
        case "status":
          return dir * a.status.localeCompare(b.status);
      }
    });
    return sorted;
  }, [rails, q, providerFilter, includeDisabled, sortKey, sortDir]);

  // Sharing detection: build a count of merchant_key_obfuscated values that
  // appear on more than one merchant. Used to highlight the row + show a
  // "shared with N merchants" badge — the explicit raison d'être of this
  // surface.
  const sharedKeyMerchants = useMemo(() => {
    const m = new Map<string, Set<number>>();
    for (const r of rails) {
      const key = r.credential?.merchant_key_obfuscated;
      if (!key || key.startsWith("(")) continue;
      if (!m.has(key)) m.set(key, new Set());
      m.get(key)!.add(r.merchant_id);
    }
    // Keep only keys that span more than one merchant.
    const shared = new Map<string, number>();
    for (const [key, merchants] of m) {
      if (merchants.size > 1) shared.set(key, merchants.size);
    }
    return shared;
  }, [rails]);

  // Distinct provider list for the filter chip row.
  const providers = useMemo(() => {
    const set = new Set<string>();
    for (const r of rails) set.add(r.provider);
    return Array.from(set).sort();
  }, [rails]);

  function toggleSort(k: SortKey) {
    if (sortKey === k) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortKey(k);
      setSortDir("asc");
    }
  }

  return (
    <div className="stack-16" style={{ padding: "0 0 32px" }}>
      <div
        className="row"
        style={{ justifyContent: "space-between", alignItems: "flex-end" }}
      >
        <div>
          <h1 style={{ margin: 0, fontSize: 22 }}>All bank rails</h1>
          <div className="dim" style={{ fontSize: 12, marginTop: 4 }}>
            Cross-tenant view. Credentials shown obfuscated as{" "}
            <span className="mono">f****l</span> — first + last rune. A row
            tinted red shares its merchant_key with another merchant.
          </div>
        </div>
        <button
          className="btn ghost sm"
          type="button"
          onClick={() => refetch()}
          disabled={isFetching}
        >
          {isFetching ? "Refreshing…" : "Refresh"}
        </button>
      </div>

      {/* Filters */}
      <div className="card flat" style={{ padding: 12 }}>
        <div className="row gap-12" style={{ flexWrap: "wrap" }}>
          <input
            className="input"
            type="text"
            placeholder="Search merchant, slug, label, base URL, obfuscated credential…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            style={{ flex: 1, minWidth: 260 }}
          />
          <select
            className="input"
            value={providerFilter}
            onChange={(e) => setProviderFilter(e.target.value)}
            style={{ width: 220 }}
          >
            <option value="">All providers</option>
            {providers.map((p) => (
              <option key={p} value={p}>
                {providerLabel(p)}
              </option>
            ))}
          </select>
          <label
            className="row gap-8"
            style={{ alignItems: "center", fontSize: 12, cursor: "pointer" }}
          >
            <input
              type="checkbox"
              checked={includeDisabled}
              onChange={(e) => setIncludeDisabled(e.target.checked)}
            />
            Include disabled
          </label>
        </div>
      </div>

      {/* Sharing-detection banner */}
      {sharedKeyMerchants.size > 0 && (
        <div
          style={{
            background: "var(--neg-soft)",
            border: "1px solid var(--neg)",
            borderRadius: 8,
            padding: "10px 14px",
            fontSize: 12,
            color: "var(--ink)",
            lineHeight: 1.5,
          }}
        >
          <strong>{sharedKeyMerchants.size}</strong> obfuscated merchant_key
          value{sharedKeyMerchants.size === 1 ? "" : "s"} appear on more than
          one merchant. Affected rows are tinted below. Investigate — same
          merchant_key across tenants either means a shared real account
          (likely fine) or a copy-paste mistake at rotation time (not fine).
        </div>
      )}

      {/* Table */}
      <div className="card flat" style={{ overflow: "auto" }}>
        <table className="tbl">
          <thead>
            <tr>
              <SortableTh
                label="Merchant"
                k="merchant"
                sortKey={sortKey}
                sortDir={sortDir}
                onClick={toggleSort}
              />
              <SortableTh
                label="Rail"
                k="label"
                sortKey={sortKey}
                sortDir={sortDir}
                onClick={toggleSort}
              />
              <SortableTh
                label="Provider"
                k="provider"
                sortKey={sortKey}
                sortDir={sortDir}
                onClick={toggleSort}
              />
              <SortableTh
                label="Env"
                k="environment"
                sortKey={sortKey}
                sortDir={sortDir}
                onClick={toggleSort}
              />
              <th>Base URL</th>
              <th>Email</th>
              <SortableTh
                label="Merchant key"
                k="merchant_key"
                sortKey={sortKey}
                sortDir={sortDir}
                onClick={toggleSort}
              />
              <th>Password</th>
              <SortableTh
                label="Status"
                k="status"
                sortKey={sortKey}
                sortDir={sortDir}
                onClick={toggleSort}
              />
              <th>Created</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td colSpan={10} className="dim" style={{ padding: 20 }}>
                  Loading…
                </td>
              </tr>
            )}
            {isError && (
              <tr>
                <td
                  colSpan={10}
                  style={{ padding: 20, color: "var(--neg)" }}
                >
                  Failed to load rails. Try refresh.
                </td>
              </tr>
            )}
            {!isLoading && !isError && view.length === 0 && (
              <tr>
                <td colSpan={10} className="dim" style={{ padding: 20 }}>
                  No rails match the current filters.
                </td>
              </tr>
            )}
            {view.map((r) => {
              const sharedCount = r.credential?.merchant_key_obfuscated
                ? sharedKeyMerchants.get(
                    r.credential.merchant_key_obfuscated,
                  ) ?? 0
                : 0;
              const isShared = sharedCount > 1;
              return (
                <RailRow
                  key={r.id}
                  rail={r}
                  isShared={isShared}
                  sharedCount={sharedCount}
                />
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="dim" style={{ fontSize: 11 }}>
        {view.length} of {rails.length} rails shown
        {!includeDisabled && " · disabled rails hidden"}
      </div>

      {/* Per-provider amount band. Lives here, next to the rails it
          governs, because "where do I change the minimum payout" had no
          answer in the panel until it did. */}
      <RailLimitsCard />
    </div>
  );
}

function SortableTh({
  label,
  k,
  sortKey,
  sortDir,
  onClick,
}: {
  label: string;
  k: SortKey;
  sortKey: SortKey;
  sortDir: "asc" | "desc";
  onClick: (k: SortKey) => void;
}) {
  const active = sortKey === k;
  return (
    <th
      onClick={() => onClick(k)}
      style={{ cursor: "pointer", userSelect: "none" }}
    >
      {label}
      {active && (
        <span className="dim" style={{ marginLeft: 4 }}>
          {sortDir === "asc" ? "▲" : "▼"}
        </span>
      )}
    </th>
  );
}

function RailRow({
  rail,
  isShared,
  sharedCount,
}: {
  rail: AdminRail;
  isShared: boolean;
  sharedCount: number;
}) {
  const c = rail.credential;
  const disabled = rail.status !== "active";
  return (
    <tr
      style={{
        background: isShared ? "var(--neg-soft)" : undefined,
        opacity: disabled ? 0.6 : 1,
      }}
    >
      <td>
        <div style={{ fontWeight: 500 }}>{rail.merchant_display_name}</div>
        <div className="dim mono" style={{ fontSize: 11 }}>
          #{rail.merchant_id} · {rail.merchant_slug}
        </div>
      </td>
      <td>
        <div>{rail.label}</div>
        <div className="dim mono" style={{ fontSize: 11 }}>
          rail #{rail.id}
        </div>
      </td>
      <td>{providerLabel(rail.provider)}</td>
      <td className="mono">{c?.environment ?? "—"}</td>
      <td
        className="mono"
        style={{
          fontSize: 11,
          maxWidth: 220,
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
        title={c?.base_url ?? ""}
      >
        {c?.base_url ?? "—"}
      </td>
      <td className="mono">{c?.email_obfuscated ?? "—"}</td>
      <td className="mono">
        {c?.merchant_key_obfuscated ?? "—"}
        {isShared && (
          <span style={{ marginLeft: 6 }}>
            <Pill tone="neg">shared × {sharedCount}</Pill>
          </span>
        )}
      </td>
      <td className="mono">{c?.password_obfuscated ?? "—"}</td>
      <td>
        <Pill tone={rail.status === "active" ? "pos" : ""}>
          {rail.status}
        </Pill>
      </td>
      <td className="dim" style={{ fontSize: 11 }}>
        {formatDateTime(rail.created_at)}
      </td>
    </tr>
  );
}
